import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, qs } from '../lib/api.js';
import { useApi, useAction, useDebounced } from '../lib/hooks.js';
import { runPhotoJob } from '../lib/photoJobs.js';
import { Loading, ErrorNote, Empty, Modal } from '../components/ui.jsx';
import AdSlot from '../components/AdSlot.jsx';

export default function Recipes() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [importing, setImporting] = useState(false);
  const debounced = useDebounced(query, 250);

  const recipes = useApi(`/recipes${qs({ q: debounced, tag })}`);
  const tags = useApi('/recipes/tags');

  return (
    <div className="stack">
      <div className="row">
        <input
          type="search"
          className="grow"
          placeholder="Search recipes…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search recipes"
        />
        <button type="button" className="primary" onClick={() => setImporting(true)}>+ Add</button>
      </div>

      <div className="row wrap" style={{ gap: 10 }}>
        <Link className="button grow" to="/cook-now">What can I cook now?</Link>
        <Link className="button grow" to="/meal-plan">Plan the week</Link>
      </div>

      <div className="row wrap" style={{ gap: 10 }}>
        {/* Req #12: the search box above looks in your own recipes; this looks
            in the curated sources for ones you do not have yet. */}
        <Link className="button grow" to="/recipes/discover">Find a new recipe online</Link>
      </div>

      {tags.data?.tags?.length > 0 && (
        <div className="chips scroll-x">
          <button type="button" className={`chip ${tag === '' ? 'active' : ''}`} onClick={() => setTag('')}>All</button>
          {tags.data.tags.slice(0, 14).map((entry) => (
            <button
              key={entry.tag}
              type="button"
              className={`chip ${tag === entry.tag ? 'active' : ''}`}
              onClick={() => setTag(tag === entry.tag ? '' : entry.tag)}
            >
              {entry.tag}
            </button>
          ))}
        </div>
      )}

      {recipes.loading && !recipes.data && <Loading />}
      <ErrorNote error={recipes.error} onRetry={recipes.reload} />

      {recipes.data?.recipes?.length === 0 && (
        <Empty
          title="No recipes yet"
          action={<button type="button" className="primary" onClick={() => setImporting(true)}>Add your first</button>}
        >
          Paste a link, photograph a cookbook page, or type one in.
        </Empty>
      )}

      <div className="stack">
        {(recipes.data?.recipes || []).map((recipe) => (
          <Link
            key={recipe.id}
            className="card row"
            to={`/recipes/${recipe.id}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {recipe.image_url
              ? <img className="thumb" src={recipe.image_url} alt="" loading="lazy" />
              : <div className="thumb" aria-hidden="true" />}
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="truncate" style={{ fontWeight: 580 }}>{recipe.title}</div>
              <div className="tiny muted truncate">
                {recipe.source_name || 'my recipes'}
                {recipe.total_minutes ? ` · ${recipe.total_minutes} min` : ''}
                {recipe.servings ? ` · serves ${recipe.servings}` : ''}
                {` · ${recipe.ingredient_count} ingredients`}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Below the list rather than inside it: an advertisement between two
          recipes reads as a recipe, and a native-looking ad is both the thing
          people resent and the thing that gets an AdSense account suspended. */}
      <AdSlot name="recipes" />

      {importing && (
        <ImportRecipe
          onClose={() => setImporting(false)}
          onImported={(recipe) => navigate(`/recipes/${recipe.id}`)}
        />
      )}
    </div>
  );
}

/** Req #9: URL or photo import, plus a manual escape hatch. */
function ImportRecipe({ onClose, onImported }) {
  const [url, setUrl] = useState('');
  const [pasted, setPasted] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const photoInput = useRef(null);
  const uploadInput = useRef(null);
  const { busy, error, setError, run } = useAction();
  const [status, setStatus] = useState(null);
  const health = useApi('/health');

  const importUrl = () => run(async () => {
    setStatus('Fetching the page…');
    try {
      const recipe = await api.post('/recipes/import/url', { url: url.trim() });
      onImported(recipe);
    } catch (err) {
      // A blocked fetch isn't a dead end — offer the path that always works.
      // A roundup is a different 422: pasting an article about thirty recipes
      // does not help, so the offer is withheld and the message stands alone.
      if (err.status === 422 && err.data?.code !== 'RECIPE_IS_COLLECTION') setShowPaste(true);
      throw err;
    } finally {
      setStatus(null);
    }
  });

  const importText = () => run(async () => {
    setStatus('Reading the recipe…');
    try {
      const recipe = await api.post('/recipes/import/text', {
        text: pasted,
        source_url: url.trim() || undefined,
      });
      onImported(recipe);
    } finally {
      setStatus(null);
    }
  });

  // Routed through the job pipeline like the other photo paths: this is the
  // longest vision call in the app (up to six pages), and the pipeline is what
  // keeps the pages out of the API request and records what the call cost.
  const importPhotos = (files) => run(async () => {
    try {
      const job = await runPhotoJob({ jobType: 'recipe_photo', files, onProgress: setStatus });
      onImported({ id: job.result_id ?? job.id });
    } finally {
      setStatus(null);
    }
  });

  // Shared by both file inputs. Resetting `value` matters: picking the same
  // file twice in a row fires no change event otherwise.
  const onPickPhotos = (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (files.length) importPhotos(files);
  };

  return (
    <Modal title="Add a recipe" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />
        {/* The refusal says what the page is; what to do about it belongs to
            whatever is showing it. This box has no button that could open a
            list up, so the way in is the publisher's own page -- and the search
            screen, where the same list can be unpacked into cards. */}
        {error?.code === 'RECIPE_IS_COLLECTION' && (
          <p className="small muted" style={{ margin: 0 }}>
            Open it, pick the recipe you want, and import that page instead &mdash; or find it
            under <Link to="/recipes/discover">Find a recipe</Link>, where a list like this can
            be opened up into the recipes it names.
          </p>
        )}
        {busy && <div className="alert info"><span className="spinner" /> {status}</div>}

        <label>
          <span className="label-text">From a link</span>
          <input
            type="url"
            inputMode="url"
            placeholder="https://www.foodnetwork.com/recipes/…"
            value={url}
            onChange={(event) => { setUrl(event.target.value); setError(null); }}
          />
        </label>
        <button type="button" className="primary full" disabled={busy || !url.trim()} onClick={importUrl}>
          Import from link
        </button>
        <p className="tiny muted" style={{ margin: 0 }}>
          Reads the site&rsquo;s own recipe markup. Works on Food Network, Bon Appétit, and most
          others; a few publishers block automated fetches, and for those you can paste instead.
        </p>

        <hr />

        {showPaste ? (
          <>
            <label>
              <span className="label-text">Paste the recipe</span>
              <textarea
                rows={8}
                value={pasted}
                onChange={(event) => setPasted(event.target.value)}
                placeholder={'Select the recipe on the page, copy, and paste it here — ingredients and steps.'}
                autoFocus
              />
            </label>
            <button
              type="button"
              className="primary full"
              disabled={busy || pasted.trim().length < 40}
              onClick={importText}
            >
              Import pasted text
            </button>
          </>
        ) : (
          <button type="button" className="full" onClick={() => setShowPaste(true)}>
            Paste a recipe instead
          </button>
        )}

        <hr />

        {/*
          Two inputs for one handler, and the only difference between them is
          `capture`. On a phone that attribute opens the rear camera directly
          with no way to reach the photo library, which is right for a cookbook
          page on the table and wrong for a recipe someone texted you. Desktop
          ignores it, so both buttons behave the same there.

          `accept` deliberately omits HEIC. That is load-bearing rather than an
          oversight: iOS converts a library photo to JPEG when the accept list
          excludes HEIC, and the server cannot analyse HEIC at all. Adding it
          here to be helpful would start handing over files that get rejected.
        */}
        <input
          ref={photoInput}
          className="photo-input"
            tabIndex={-1}
            aria-hidden="true"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          capture="environment"
          onChange={onPickPhotos}
        />
        <input
          ref={uploadInput}
          className="photo-input"
            tabIndex={-1}
            aria-hidden="true"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={onPickPhotos}
        />
        <button
          type="button"
          className="full"
          disabled={busy || (health.data && !health.data.vision)}
          onClick={() => photoInput.current?.click()}
        >
          Photograph a recipe
        </button>
        <button
          type="button"
          className="full"
          disabled={busy || (health.data && !health.data.vision)}
          onClick={() => uploadInput.current?.click()}
        >
          Upload a photo or screenshot
        </button>
        <p className="tiny muted" style={{ margin: 0 }}>
          Cookbook page, index card, handwriting &mdash; or a screenshot of a recipe
          someone sent you. Select several for a multi-page recipe.
        </p>

        <hr />

        <Link className="button full" to="/recipes/new" onClick={onClose}>Type one in</Link>
      </div>
    </Modal>
  );
}
